import { useMemo, useState } from 'react';
import { AutocompleteInput } from '@/components';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useDebounce } from '@/hooks';
import {
  useGetNeighborhoodsQuery,
  useGetNeighborhoodByIdQuery,
} from '@/pages/Business/pages/Commerce/services';
import { Neighborhood } from '@/pages/Business/pages/Commerce/models';

interface NeighborhoodAutocompleteProps {
  value: string;
  onChange: (neighborhoodId: string) => void;
}

const isNeighborhood = (data: unknown): data is Neighborhood =>
  typeof data === 'object' && data !== null && 'id' in data;

const NeighborhoodAutocomplete = ({
  value,
  onChange,
}: NeighborhoodAutocompleteProps) => {
  const [input, setInput] = useState('');
  const debouncedInput = useDebounce(input, 400);

  const { data, isFetching } = useGetNeighborhoodsQuery(
    { paginated: { page: 1, pageSize: 20 }, search: debouncedInput },
    { skip: debouncedInput.trim().length < 3 },
  );

  const { data: currentNeighborhood } = useGetNeighborhoodByIdQuery(
    { neighborhoodId: value },
    { skip: !value },
  );

  const options: Option[] = useMemo(
    () =>
      (data?.items ?? []).map((neighborhood) => ({
        label: neighborhood.name,
        data: neighborhood,
      })),
    [data],
  );

  const selectedOption: Option | null = value
    ? {
        label: currentNeighborhood?.name ?? 'Barrio seleccionado',
        data: currentNeighborhood ?? value,
      }
    : null;

  const handleChange = (option: Option | null) => {
    if (option && isNeighborhood(option.data)) {
      onChange(option.data.id);
      return;
    }
    onChange('');
  };

  return (
    <AutocompleteInput
      name='neighborhoodId'
      placeholder='Barrio (opcional)'
      value={selectedOption}
      onChange={handleChange}
      onInputChange={setInput}
      options={options}
      loading={isFetching}
    />
  );
};

export default NeighborhoodAutocomplete;
